import { CommonModule } from '@angular/common'
import { Overlay, OverlayModule, type OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import {
    AfterViewChecked,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewContainerRef,
    forwardRef,
    ContentChild,
    ElementRef,
    OnDestroy,
    TemplateRef,
    inject,
} from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { IDropdownConfig, IDropdownOption } from '../../models/dropdown.model'
import { EiSearchBarComponent } from '../ei-search-bar/ei-search-bar.component'
import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import { USER_FIELD_VALIDATION } from '@app/features/school-site/constants/id-creation.constants'
import {
    getFieldMaxLengthErrorMessage,
    sanitizeTextByFilter,
    truncateToMaxLength,
    wouldExceedMaxLengthAfterInsert,
} from '@app/features/school-site/helpers/input-field.helper'
import { EI_INPUT_FIELD_CONSTANTS } from '@app/shared/constants/ei-input-field.constants'
import type { TInputFieldTextFilter } from '@app/features/school-site/shared/types/input-field.types'
import { EDropdownTriggerMode } from '@app/shared/enums/ei-dropdown.enum'
import { SvgIconComponent } from '@app/shared/components/svg-icon/svg-icon.component'

type TDropdownValue = string | number
type TDropdownFormValue = TDropdownValue | TDropdownValue[] | null

@Component({
    selector: 'ei-dropdown',
    standalone: true,
    imports: [CommonModule, EiSearchBarComponent, FormsModule, SvgIconComponent, OverlayModule],
    templateUrl: './ei-dropdown.component.html',
    styleUrl: './ei-dropdown.component.scss',
    host: {
        '(document:click)': 'onDocumentClick($event)',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EiDropdownComponent),
            multi: true,
        },
    ],
})
export class EiDropdownComponent
    implements ControlValueAccessor, OnDestroy, OnChanges, AfterViewChecked
{
    private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef)
    private readonly overlay: Overlay = inject(Overlay)
    private readonly vcr: ViewContainerRef = inject(ViewContainerRef)

    private static openedDropdown?: EiDropdownComponent
    private static instanceCount = 0

    private readonly generatedTriggerId = `ei-dropdown-trigger-${++EiDropdownComponent.instanceCount}`

    private overlayRef: OverlayRef | null = null
    /** Cleanup for scroll listener on scrollable ancestor (so overlay repositions on scroll). */
    private scrollCleanup: (() => void) | null = null

    readonly DropdownTriggerMode = EDropdownTriggerMode

    @Input() config!: IDropdownConfig
    /**
     * Icon triggers render no text, so they are named by this label. Default triggers are named
     * by their associated `<label>` — an aria-label there would mask the selected value — so this
     * is ignored unless `triggerMode` is `Icon`.
     */
    @Input() triggerAriaLabel = 'Open dropdown'
    /**
     * Id applied to the trigger button so an external `<label for="...">` can name it. The custom
     * element host is not labelable, so putting `id` on `<ei-dropdown>` does not associate a label.
     * Defaults to a generated id, which the internal `config.label` uses.
     */
    @Input() triggerId?: string
    @Input() triggerMode: EDropdownTriggerMode = EDropdownTriggerMode.Default
    /** Ensures panel isn't tiny when trigger is icon-sized. */
    @Input() overlayMinWidthPx = 0
    /** When true, panel opens above the trigger (e.g. for footers inside overflow containers). */
    @Input() openAbove = false

    @ViewChild('triggerButton', { read: ElementRef })
    private readonly triggerButton?: ElementRef<HTMLButtonElement>

    @ViewChild('panelTemplate', { read: TemplateRef })
    private panelTemplate?: TemplateRef<unknown>

    @ContentChild('dropdownTrigger') customTrigger?: TemplateRef<{
        toggle: () => void
        isOpen: boolean
    }>
    @ContentChild('dropdownPanel') customPanel?: TemplateRef<{
        close: () => void
    }>

    isDropdownOpen = false
    isControlDisabled = false

    panelMinWidthPx = 0
    private panelMaxWidthPx = 0

    optionsSelected: TDropdownValue[] = []
    optionSelected: TDropdownValue | null = null
    searchValue = ''
    newOptionValue = ''
    createNewInvalidMessage = ''

    private readonly positions = [
        {
            originX: 'start' as const,
            originY: 'bottom' as const,
            overlayX: 'start' as const,
            overlayY: 'top' as const,
            offsetY: 4,
        },
        {
            originX: 'end' as const,
            originY: 'bottom' as const,
            overlayX: 'end' as const,
            overlayY: 'top' as const,
            offsetY: 4,
        },
        {
            originX: 'start' as const,
            originY: 'top' as const,
            overlayX: 'start' as const,
            overlayY: 'bottom' as const,
            offsetY: -4,
        },
        {
            originX: 'end' as const,
            originY: 'top' as const,
            overlayX: 'end' as const,
            overlayY: 'bottom' as const,
            offsetY: -4,
        },
    ]

    get isMultiSelect(): boolean {
        return !!this.config?.isMultiSelect
    }

    get isDisabled(): boolean {
        return !!this.isControlDisabled || !!this.config?.isDisabled
    }

    ngAfterViewChecked(): void {
        if (!this.isDropdownOpen || !this.overlayRef) return

        const triggerEl = this.getTriggerElement()
        const { minWidthPx, maxWidthPx } = this.getOverlayWidthBounds(triggerEl)
        const sizeChanged =
            minWidthPx !== this.panelMinWidthPx || maxWidthPx !== this.panelMaxWidthPx

        if (sizeChanged) {
            this.panelMinWidthPx = minWidthPx
            this.panelMaxWidthPx = maxWidthPx
            this.overlayRef.updateSize({
                minWidth: minWidthPx || undefined,
                maxWidth: maxWidthPx || undefined,
            })
        }

        this.overlayRef.updatePosition()
    }

    ngOnDestroy(): void {
        this.destroyOverlay()
        if (EiDropdownComponent.openedDropdown === this) {
            EiDropdownComponent.openedDropdown = undefined
        }
    }

    onDocumentClick(event: MouseEvent): void {
        if (!this.isDropdownOpen) return
        const target = event.target as Node | null
        if (!target) return
        if (this.hostRef.nativeElement.contains(target)) return
        if (this.overlayRef?.overlayElement?.contains(target)) return
        this.closeDropdown()
    }

    onTriggerClick(event: MouseEvent): void {
        event.stopPropagation()
        this.toggleDropdown()
    }

    toggleDropdown(): void {
        if (this.isDisabled) return
        if (this.isDropdownOpen) {
            this.closeDropdown()
        } else {
            this.openDropdown()
        }
    }

    openDropdown(): void {
        if (this.isDisabled) return
        if (EiDropdownComponent.openedDropdown && EiDropdownComponent.openedDropdown !== this) {
            EiDropdownComponent.openedDropdown.closeDropdown()
        }
        const triggerEl = this.getTriggerElement()
        const { minWidthPx, maxWidthPx } = this.getOverlayWidthBounds(triggerEl)
        this.panelMinWidthPx = minWidthPx
        this.panelMaxWidthPx = maxWidthPx
        this.isDropdownOpen = true
        EiDropdownComponent.openedDropdown = this

        if (this.panelTemplate) {
            this.destroyOverlay()
            const positionStrategy = this.overlay
                .position()
                .flexibleConnectedTo(this.getOverlayOrigin())
                .withPositions(this.positions)
                .withPush(false)
                .withViewportMargin(8)

            const overlayRef = (this.overlayRef = this.overlay.create({
                positionStrategy,
                scrollStrategy: this.overlay.scrollStrategies.reposition(),
                hasBackdrop: false,
                minWidth: this.panelMinWidthPx || undefined,
                maxWidth: this.panelMaxWidthPx || undefined,
                maxHeight: this.config?.allowCreateNew ? undefined : 400,
            }))
            const portal = new TemplatePortal(this.panelTemplate, this.vcr)
            overlayRef.attach(portal)
            overlayRef.updatePosition()
            // Re-check after the click/blur cycle settles so sibling layout changes don't stale the anchor.
            requestAnimationFrame(() => {
                if (this.isDropdownOpen) {
                    this.overlayRef?.updatePosition()
                }
            })
            this.attachScrollReposition(triggerEl)
        }
    }

    /** Reposition overlay when a scrollable ancestor scrolls (e.g. modal body). */
    private attachScrollReposition(triggerEl: HTMLElement): void {
        this.clearScrollReposition()
        const scrollContainer = this.findScrollContainer(triggerEl)
        if (!scrollContainer) return
        const onScroll = (): void => {
            this.overlayRef?.updatePosition()
        }
        scrollContainer.addEventListener('scroll', onScroll, { passive: true })
        this.scrollCleanup = () => {
            scrollContainer.removeEventListener('scroll', onScroll)
            this.scrollCleanup = null
        }
    }

    private clearScrollReposition(): void {
        if (this.scrollCleanup) {
            this.scrollCleanup()
            this.scrollCleanup = null
        }
    }

    closeDropdown(): void {
        this.isDropdownOpen = false
        this.searchValue = ''
        this.newOptionValue = ''
        this.createNewInvalidMessage = ''
        this.destroyOverlay()
        this.onTouched()
        if (EiDropdownComponent.openedDropdown === this) {
            EiDropdownComponent.openedDropdown = undefined
        }
    }

    private destroyOverlay(): void {
        this.clearScrollReposition()
        if (this.overlayRef) {
            this.overlayRef.detach()
            this.overlayRef.dispose()
            this.overlayRef = null
        }
    }

    private getTriggerElement(): HTMLElement {
        return this.triggerButton?.nativeElement ?? this.hostRef.nativeElement
    }

    private getOverlayOrigin(): HTMLElement {
        if (!this.customTrigger && this.triggerMode === EDropdownTriggerMode.Default) {
            return this.hostRef.nativeElement
        }
        return this.getTriggerElement()
    }

    private getOverlayWidthBounds(triggerEl: HTMLElement): {
        minWidthPx: number
        maxWidthPx: number
    } {
        const triggerRect = triggerEl.getBoundingClientRect()
        const minWidthPx = Math.max(triggerRect.width ?? 0, this.overlayMinWidthPx || 0)
        const containerRight = this.getBoundingContainerRight(triggerEl)
        const margin = 16
        const maxWidthPx = Math.max(minWidthPx, containerRight - triggerRect.left - margin)

        return {
            minWidthPx,
            maxWidthPx,
        }
    }

    /**
     * Right edge (in viewport coordinates) of the trigger's bounding container,
     * so the overlay can be constrained to the parent's width. Uses the nearest
     * scroll container, else offsetParent, else viewport.
     */
    private getBoundingContainerRight(triggerEl: HTMLElement): number {
        const scrollContainer = this.findScrollContainer(triggerEl)
        if (scrollContainer) {
            return scrollContainer.getBoundingClientRect().right
        }
        const offsetParent = triggerEl.offsetParent as HTMLElement | null
        if (offsetParent) {
            return offsetParent.getBoundingClientRect().right
        }
        return window.innerWidth
    }

    private findScrollContainer(el: HTMLElement): HTMLElement | null {
        let parent = el.parentElement
        while (parent) {
            const { overflow, overflowX, overflowY } = getComputedStyle(parent)
            const overflowStr = overflow + overflowX + overflowY
            if (
                overflowStr.includes('auto') ||
                overflowStr.includes('scroll') ||
                overflowStr.includes('overlay')
            ) {
                return parent
            }
            parent = parent.parentElement
        }
        return null
    }

    // get displayText(): string {
    //     if (!this.hasValue) return this.config?.placeholder ?? ''
    //     if (this.isMultiSelect) {
    //         if (this.optionsSelected.length === 1) {
    //             return this.getLabelForValue(this.optionsSelected[0] ?? null)
    //         }
    //         return ''
    //     }
    //     return this.getLabelForValue(this.optionSelected)
    // }

    /** Resolves option value to display label from config.options; falls back to value if no label. */
    private getLabelForValue(value: TDropdownValue | null): string {
        if (value == null) return ''
        const option = (this.config?.options ?? []).find((opt) => opt.value === value)
        return option != null && option.label != null ? String(option.label) : String(value)
    }

    displayText = ''

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['config']) {
            this.updateDisplayText()
        }
    }

    private updateDisplayText(): void {
        if (!this.hasValue) {
            this.displayText = this.config?.placeholder ?? ''
            return
        }

        if (this.isMultiSelect) {
            this.displayText =
                this.optionsSelected.length === 1
                    ? this.resolveLabelForValue(this.optionsSelected[0])
                    : ''
            return
        }

        this.displayText = this.resolveLabelForValue(this.optionSelected)
    }

    private resolveLabelForValue(value: TDropdownValue | null): string {
        if (value === null) return ''
        const option = this.config?.options?.find((opt) => opt.value === value)
        return option?.label ?? value?.toString() ?? ''
    }

    // Legacy display-text logic kept here for reference while selection behavior evolves.
    // get displayText(): string {
    //     if (!this.hasValue) return this.config?.placeholder ?? ''
    //     if (this.isMultiSelect) {
    //         if (this.optionsSelected.length === 1) {
    //             const option = this.findOptionByValue(this.optionsSelected[0])
    //             return option?.label?.toString() ?? this.optionsSelected[0]?.toString() ?? ''
    //         }
    //         return ''
    //     }
    //     const option = this.findOptionByValue(this.optionSelected)
    //     return option?.label?.toString() ?? this.optionSelected?.toString() ?? ''
    // }

    isOptionSelected(option: IDropdownOption): boolean {
        return this.isMultiSelect
            ? this.optionsSelected.includes(option.value)
            : this.optionSelected === option.value
    }

    toggleSelection(option: IDropdownOption): void {
        if (this.isDisabled || option.isDisabled) return

        if (this.isMultiSelect) {
            if (this.optionsSelected.includes(option.value)) {
                this.optionsSelected = this.optionsSelected.filter(
                    (value) => value !== option.value,
                )
            } else {
                this.optionsSelected = [...this.optionsSelected, option.value]
            }
            this.onTouched()
        } else {
            this.optionSelected = this.optionSelected === option.value ? null : option.value
            this.closeDropdown()
        }

        this.updateDisplayText()
        this.onTouched()
        this.onChange(this.getValueForForm())
    }

    writeValue(value: TDropdownFormValue): void {
        if (this.isMultiSelect) {
            this.optionsSelected = value ? (Array.isArray(value) ? value : [value]) : []
        } else {
            this.optionSelected = value ? (value as string | number) : null
        }
        this.updateDisplayText()
    }

    registerOnChange(fn: (value: string | number | (string | number)[] | null) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.isControlDisabled = isDisabled
    }

    private onChange: (value: TDropdownFormValue) => void = () => {}
    private onTouched: () => void = () => {}

    get hasValue(): boolean {
        return this.isMultiSelect ? this.optionsSelected.length > 0 : !!this.optionSelected
    }

    shouldShowLabel(): boolean {
        return !!this.config?.label
    }

    /** Id of the trigger button — the element a `<label for="...">` must point at. */
    get triggerElementId(): string {
        return this.triggerId ?? this.generatedTriggerId
    }

    getPlaceholderParts(placeholder: string): { text: string; asterisk: string } {
        const match = placeholder.match(REGEX_PATTERNS.PLACEHOLDER_TEXT_TRAILING_ASTERISKS)
        if (match) {
            return { text: match[1], asterisk: match[2] ?? '' }
        }
        return { text: placeholder, asterisk: '' }
    }

    private getValueForForm(): string | number | (string | number)[] | null {
        return this.isMultiSelect ? [...this.optionsSelected] : this.optionSelected
    }

    get multiPrimaryText(): string {
        if (!this.isMultiSelect) return ''
        if (!this.optionsSelected?.length) return ''

        const maxDisplay = 2
        const displayCount = Math.min(this.optionsSelected.length, maxDisplay)
        const displayValues = this.optionsSelected
            .slice(0, displayCount)
            .map((value) => this.getLabelForValue(value))
            .filter((label) => label.length > 0)

        return displayValues.join(', ')
    }

    get multiExtraCount(): number {
        if (!this.isMultiSelect) return 0
        return Math.max(0, this.optionsSelected.length - 2)
    }

    private findOptionByValue(value: string | number | null): IDropdownOption | undefined {
        if (value === null || value === undefined) return undefined
        return this.config?.options?.find((option) => option.value === value)
    }

    get enabledOptions(): IDropdownOption[] {
        return (this.config?.options ?? []).filter((option) => !option.isDisabled)
    }

    private get filteredEnabledValues(): (string | number)[] {
        return this.filteredOptions
            .filter((option) => !option.isDisabled)
            .map((option) => option.value)
    }

    get isAllSelected(): boolean {
        const values = this.filteredEnabledValues
        return values.length > 0 && values.every((value) => this.optionsSelected.includes(value))
    }

    get isSomeSelected(): boolean {
        const values = this.filteredEnabledValues
        const anySelected = values.some((value) => this.optionsSelected.includes(value))
        return anySelected && !this.isAllSelected
    }

    toggleSelectAll(): void {
        if (!this.isMultiSelect || this.isDisabled) return

        const filteredEnabledValues = this.filteredOptions
            .filter((option) => !option.isDisabled)
            .map((option) => option.value)

        if (!filteredEnabledValues.length) return

        const isAllFilteredSelected = filteredEnabledValues.every((value) =>
            this.optionsSelected.includes(value),
        )

        this.optionsSelected = isAllFilteredSelected
            ? this.optionsSelected.filter((value) => !filteredEnabledValues.includes(value)) // remove only filtered
            : Array.from(new Set([...this.optionsSelected, ...filteredEnabledValues])) // add filtered, keep existing

        this.updateDisplayText()
        this.onTouched()
        this.onChange(this.getValueForForm())
    }

    get filteredOptions(): IDropdownOption[] {
        const query = this.searchValue.trim().toLowerCase()
        const options = this.config?.options ?? []
        if (!query) return options
        return options.filter((option) => {
            const valueText = option.value.toString().toLowerCase()
            const labelText = (option.label ?? '').toString().toLowerCase()
            return valueText.includes(query) || labelText.includes(query)
        })
    }

    get showCreateNewSection(): boolean {
        return !!this.config?.allowCreateNew
    }

    get createNewInputValue(): string {
        return this.newOptionValue
    }

    get isCreateNewEnabled(): boolean {
        return !!this.createNewInputValue.trim()
    }

    onCreateNew(): void {
        const newValue = this.createNewInputValue.trim()
        if (!newValue || this.isDisabled) return

        // Check if option already exists (case-insensitive)
        const existingOption = this.config?.options.find(
            (option) => option.value.toString().toLowerCase() === newValue.toLowerCase(),
        )

        if (existingOption) {
            // If it exists, just select it
            this.toggleSelection(existingOption)
            return
        }

        // Call the callback to create the new option
        if (this.config?.onCreateNew) {
            this.config.onCreateNew(newValue)
        }

        // Clear the new option input and close dropdown
        this.newOptionValue = ''
        this.searchValue = ''
        // Close dropdown after a brief delay to allow parent to update options
        setTimeout(() => {
            this.closeDropdown()
        }, 0)
    }

    onCreateNewKeyDown(event: KeyboardEvent): void {
        if (this.isCreateNewNavigationOrShortcutKey(event)) {
            return
        }
        if (event.key.length !== 1) {
            return
        }
        if (this.blockCreateNewMaxLengthKeyDown(event)) {
            return
        }
        const filter = this.config?.createNewTextFilter
        if (!filter) {
            this.createNewInvalidMessage = ''
            return
        }
        if (!this.isCreateNewKeyAllowed(event.key, filter)) {
            this.createNewInvalidMessage = this.getCreateNewFilterMessage(filter)
            event.preventDefault()
            return
        }
        this.createNewInvalidMessage = ''
    }

    onCreateNewPaste(event: ClipboardEvent): void {
        const filter = this.config?.createNewTextFilter
        const maxLength = this.config?.createNewMaxLength
        if (!filter && maxLength == null) {
            return
        }

        event.preventDefault()
        const pasted = event.clipboardData?.getData('text') ?? ''
        if (!pasted) {
            return
        }

        const target = event.target as HTMLInputElement
        const start = target.selectionStart ?? 0
        const end = target.selectionEnd ?? 0
        const current = target.value
        const filtered = filter ? sanitizeTextByFilter(pasted, filter) : pasted
        let merged = current.slice(0, start) + filtered + current.slice(end)
        if (filter) {
            merged = sanitizeTextByFilter(merged, filter)
        }

        const beforeMaxLength = merged
        if (maxLength != null) {
            merged = truncateToMaxLength(merged, maxLength)
        }

        if (maxLength != null && merged.length < beforeMaxLength.length) {
            this.createNewInvalidMessage = this.getCreateNewMaxLengthErrorMessage()
        } else if (filter && filtered !== pasted) {
            this.createNewInvalidMessage = this.getCreateNewFilterMessage(filter)
        } else {
            this.createNewInvalidMessage = ''
        }

        this.newOptionValue = merged
        const pos = Math.min(start + filtered.length, merged.length)
        setTimeout(() => target.setSelectionRange(pos, pos), 0)
    }

    onNewOptionInputChange(value: string): void {
        let next = value
        const filter = this.config?.createNewTextFilter
        if (filter) {
            next = sanitizeTextByFilter(value, filter)
        }

        let maxLengthExceeded = false
        if (this.config?.createNewMaxLength != null) {
            const truncated = truncateToMaxLength(next, this.config.createNewMaxLength)
            if (truncated.length < next.length) {
                this.createNewInvalidMessage = this.getCreateNewMaxLengthErrorMessage()
                maxLengthExceeded = true
            }
            next = truncated
        }

        if (!maxLengthExceeded) {
            this.createNewInvalidMessage = ''
        }
        this.newOptionValue = next
    }

    private getCreateNewMaxLengthErrorMessage(): string {
        return getFieldMaxLengthErrorMessage(
            this.config?.createNewLabel ?? this.config?.label ?? 'Value',
            this.config?.createNewMaxLength ?? 0,
        )
    }

    private isCreateNewNavigationOrShortcutKey(event: KeyboardEvent): boolean {
        const allowedKeys = EI_INPUT_FIELD_CONSTANTS.ALLOWED_PHONE_KEYS
        const shortcuts = EI_INPUT_FIELD_CONSTANTS.KEYBOARD_SHORTCUTS
        const keyLower = event.key.toLowerCase()
        const isCtrlA = keyLower === shortcuts.SELECT_ALL && (event.ctrlKey || event.metaKey)
        const isCtrlC = keyLower === shortcuts.COPY && (event.ctrlKey || event.metaKey)
        const isCtrlV = keyLower === shortcuts.PASTE && (event.ctrlKey || event.metaKey)
        const isCtrlX = keyLower === shortcuts.CUT && (event.ctrlKey || event.metaKey)
        const isCtrlZ = keyLower === shortcuts.UNDO && (event.ctrlKey || event.metaKey)

        return (
            allowedKeys.includes(event.key as (typeof allowedKeys)[number]) ||
            isCtrlA ||
            isCtrlC ||
            isCtrlV ||
            isCtrlX ||
            isCtrlZ
        )
    }

    private blockCreateNewMaxLengthKeyDown(event: KeyboardEvent): boolean {
        const maxLength = this.config?.createNewMaxLength
        if (maxLength == null) {
            return false
        }

        const target = event.target as HTMLInputElement
        const selectionStart = target.selectionStart ?? 0
        const selectionEnd = target.selectionEnd ?? 0
        if (
            !wouldExceedMaxLengthAfterInsert(
                target.value,
                selectionStart,
                selectionEnd,
                1,
                maxLength,
            )
        ) {
            return false
        }

        this.createNewInvalidMessage = this.getCreateNewMaxLengthErrorMessage()
        event.preventDefault()
        return true
    }

    private isCreateNewKeyAllowed(key: string, filter: TInputFieldTextFilter): boolean {
        switch (filter) {
            case 'lettersOnly':
                return REGEX_PATTERNS.LETTERS_ONLY_SINGLE_UNICODE_LETTER.test(key)
            case 'namePart':
                return REGEX_PATTERNS.NAME_PART_ALLOWED_SINGLE_KEY.test(key)
            case 'digitsOnly':
                return REGEX_PATTERNS.SINGLE_DIGIT.test(key)
            case 'section':
                return REGEX_PATTERNS.SECTION_OTHER_ALLOWED_SINGLE_KEY.test(key)
            case 'alphanumericOnly':
                return REGEX_PATTERNS.ALPHANUMERIC_SINGLE_KEY.test(key)
            default:
                return true
        }
    }

    private getCreateNewFilterMessage(filter: TInputFieldTextFilter): string {
        const fieldLabel = this.config?.createNewLabel ?? this.config?.label ?? 'Value'
        switch (filter) {
            case 'namePart':
                return USER_FIELD_VALIDATION.messages.nameInvalidChars(fieldLabel)
            case 'digitsOnly':
                return USER_FIELD_VALIDATION.messages.rollInvalidChars
            case 'section':
                return USER_FIELD_VALIDATION.messages.sectionInvalidChars
            case 'alphanumericOnly':
                return USER_FIELD_VALIDATION.messages.enrollmentInvalidChars
            default:
                return USER_FIELD_VALIDATION.messages.nameInvalidChars(fieldLabel)
        }
    }

    getHighlightedText(optionValue: string): { before: string; match: string; after: string } {
        const query = this.searchValue.trim().toLowerCase()
        if (!query) {
            return { before: optionValue, match: '', after: '' }
        }

        const valueStr = optionValue.toString()
        const lowerValue = valueStr.toLowerCase()
        const index = lowerValue.indexOf(query)

        if (index === -1) {
            return { before: valueStr, match: '', after: '' }
        }

        return {
            before: valueStr.substring(0, index),
            match: valueStr.substring(index, index + query.length),
            after: valueStr.substring(index + query.length),
        }
    }
}
